import React, { useState } from 'react';
import {
  Button,
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tooltip,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  DatePicker,
  LocalizationProvider
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Add, AccountBox, Search, SearchOff, UploadFile, QrCode, QrCodeScanner } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import QRPassDialogsChain from '../components/QRPassDialogsChain';
import QRPassDisplayDialog from '../components/QRPassDisplayDialog';
import { validateCustomer } from '../validate/validateCustomer';
import { getValidationErrors } from '../validate/validationErrors';
import TableComponent from '../components/TableComponent';
import API from '../services/api';
import downloadFile from '../services/download';
import dateFns, { format } from 'date-fns';

const CustomersPage = () => {

  const navigate = useNavigate();

  const [forceUpdateCounter, setForceUpdateCounter] = useState(0);
  const [resetPage, setResetPage] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedQRPass, setSelectedQRPass] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [isQRPassCreateOpen, setIsQRPassCreateOpen] = useState(false);
  const [isQRPassDisplayOpen, setIsQRPassDisplayOpen] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  const columns = [
    { id: 'expand', label: '', width: '5%' },
    { id: 'name', label: 'Name', width: '24%', action: {
        handler: (row) => handleProfileClick(row)
    }},
    { id: 'phone', label: 'Phone', width: '18%' },
    { id: 'passesCount', label: 'Passes', width: '5%' },
    { id: 'email', label: 'Email', width: '18%' },
    { id: 'comment', label: 'Comment', width: '20%' },
    { id: 'actions', label: '', width: '10%' }
  ];

  const actionsHandlers = {
    actions: [
      {
        name: 'profile',
        label: <AccountBox />,
        tooltip: 'Customer Profile',
        handler: (row) => handleProfileClick(row)
      },
      {
        name: 'createPass',
        label: <QrCode />,
        tooltip: 'Create Pass',
        handler: (row) => handleCreatePassClick(row)
      }
    ]
  };

  const emptySearchValues = {
    name: '',
    phone: '',
    email: '',
    comment: ''
  }

  const [searchValues, setSearchValues] = useState({
      ...emptySearchValues
    });
  
  const [searchValuesPrepared, setSearchValuesPrepared] = useState({
      ...emptySearchValues
    });

   const emptyExportValues = {
    fromDate: null,
    toDate: null,
    activePass: false,
  }

  const [exportValues, setExportValues] = useState({
      ...emptyExportValues
    });

  const getData = async (page, limit) => {
    // PassLogs loading
    const params = new URLSearchParams();
    if (searchValues.name) {
      params.append('name', searchValues.name);
    }
    if (searchValues.phone) {
      params.append('phone', searchValues.phone);
    }
    if (searchValues.email) {
      params.append('email', searchValues.email);
    }
    if (searchValues.comment) {
      params.append('comment', searchValues.comment);
    }
    params.append('page', page);
    params.append('limit', limit);
    const response = await API.get(`/api/customers?${params.toString()}`);

    // Data processing
    const processedData = {
      ...response.data,
      rows: response.data.customers.map(customer => {
          customer.qrCodes = customer.qrCodes.map(qrCode => {
            let active = qrCode.enabled && qrCode.daysLeft > 0;
            return {
                ...qrCode,
                active: active
            }});
            return {
            ...customer,
            passesCount: customer.qrCodes.length,
        }})
    };

    delete processedData.qrCodes;

    return processedData;
  };

  const updateData = () => {
    setForceUpdateCounter(prev => prev + 1);
  }

  const handleProfileClick = (row) => {
    navigate(`/customers/${row.id}/profile`, {
      state: { from: location.pathname }
    });
  };

  const handleCreateSave = () => {

    // Validation stage
    try {
      const { error } = validateCustomer(editValues);
        if (error) {
          setValidationErrors(getValidationErrors(error));
          console.log(`ValidationErrors: ${JSON.stringify(validationErrors)}`)
          return;
        }
        setValidationErrors({});
    } catch (err) {
      console.log(`Exception during validation: ${err}`);
      return;
    }

    API.post('/api/customers', editValues)
        .then((response) => {
            setOpenCreateDialog(false);
            setError(null);
            // open profile page of newly created customer
            handleProfileClick(response.data);
        })
        .catch(error => {
          if (error.response && error.response.data && error.response.data.msg) {
            setError(error.response.data.msg);
          } else {
            setError(error.message);
          }
          console.error('Error when creating customer:', error)
        });
  };

  const handleCreateCancel = () => {
    setError(null);
    setValidationErrors({});
    setOpenCreateDialog(false);
  };

  const handleCreateClick = () => {
    setSelectedRow(null);
    setEditValues({name: '', phone: '', email: '', comment: '', whatsapp: ''});
    setOpenCreateDialog(true);
  };

  const handleChange = (field, value) => {
    setEditValues({
      ...editValues,
      [field]: value
    });
  };

  const handleSearch = () => {
    // If search values are not changed and user just clicked button search
    const isChanged = Object.keys(searchValuesPrepared).some(key =>
      searchValues[key] !== searchValuesPrepared[key]
      );
    if (!isChanged) return;

    setSearchValues({...searchValuesPrepared});
    setResetPage(true);
    updateData();
  }

  const handleSearchChange = (field, value) => {
    setSearchValuesPrepared({
        ...searchValuesPrepared,
        [field]: value
    });
  };

  const handleExportChange = (field, value) => {
    setExportValues({
        ...exportValues,
        [field]: value
    });
  };

  const resetSearch = () => {
    // Always clean text in search fields
    setSearchValuesPrepared({...emptySearchValues});

    // If search was not run before and current search values are empty
    const isChanged = Object.keys(emptySearchValues).some(key =>
      searchValues[key] !== emptySearchValues[key]
      );
    if (!isChanged) return;

    setSearchValues({...emptySearchValues});
    setResetPage(true);
    updateData();
  };

  const handleCreatePassClick = (row) => {
    setSelectedRow(row);
    setIsQRPassCreateOpen(true);
  };

  const handleQRPassDisplayClick = (row, qrPass) => {
     API.get(`/api/passes/${qrPass.id}`)
    .then(response => {
      const qrPass = response.data;
      setSelectedRow(row);
      setSelectedQRPass(qrPass);
      setIsQRPassDisplayOpen(true);
      setError(null);
    })
    .catch(error => {
      if (error.response && error.response.data && error.response.data.msg) {
        setError(error.response.data.msg);
      } else {
        setError(error.message);
      }
      console.error('Error when gettings QRCode:', error)
    });
  }

  const expandedTableColumns = [
    { id: 'startDate', label: 'Start Date', type: 'date' },
    { id: 'endDate', label: 'End Date', type: 'date' },
    { id: 'daysLeft', label: 'Days Left' },
    { id: 'amount', label: 'Amount', type: 'currency' },
    { id: 'actions', label: '' }
  ];

  const expandedTableActionsHandlers = (row) => ({
    actions: [
      {
        name: 'show',
        label: <QrCodeScanner/>,
        tooltip: 'Show Pass',
        handler: (qrCode) => handleQRPassDisplayClick(row, qrCode)
      },
    ]
  });

  const getExpandedTableData = async (row, page, limit) => {
    return {
        page: 0,
        limit: 1000,
        totalCount: row.qrCodes.length,
        totalPages: 1,
        rows: row.qrCodes
    };
  };

  const handleExportDialogCancel = () => {
    setError(null);
    setExportValues(emptyExportValues);
    setOpenExportDialog(false);
  };

  const handleExportDialogClick = () => {
    const params = new URLSearchParams();
    if (exportValues.fromDate) {
      params.append('fromDate', format(exportValues.fromDate, 'yyyy-MM-dd'));
    }
    if (exportValues.toDate) {
      params.append('toDate', format(exportValues.toDate, 'yyyy-MM-dd'));
    }
    if (exportValues.activePass) {
      params.append('activePass', exportValues.activePass);
    }
    setOpenExportDialog(false);
    setError(null);
    setExportValues(emptyExportValues);
    
    downloadFile(`/api/customers/exportData?${params.toString()}`, 'Customers.xlsx');
  };

  const handleExportDataClick = () => {
    setOpenExportDialog(true);
  };

  const ExpandedContent = ({ row }) => {
    return (
      <Box sx={{ padding: 2, ml: 8 }}>
          <TableComponent
            columns={expandedTableColumns}
            getData={(page, limit) => getExpandedTableData(row, page, limit)}
            actionsHandlers={expandedTableActionsHandlers(row)}
            pagination={false}
            customHeaderCellStyle={{ backgroundColor: 'none' }}
            tableComponent={Box}
          />
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: '20px',
        margin: 'auto',
        width: '80%'
      }}
    >
      <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            margin: 'auto',
            gap: '10px',
            marginBottom: '20px', 
        }}
        >
        <TextField
            label="Name"
            value={searchValuesPrepared.name}
            onChange={(e) => handleSearchChange('name', e.target.value)}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                handleSearch();
                }
            }}
            fullWidth
            size="small"
        />
        <TextField
            label="Phone"
            value={searchValuesPrepared.phone}
            onChange={(e) => handleSearchChange('phone', e.target.value)}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                handleSearch();
                }
            }}
            fullWidth
            size="small"
        />
        <TextField
            label="Email"
            value={searchValuesPrepared.email}
            onChange={(e) => handleSearchChange('email', e.target.value)}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                handleSearch();
                }
            }}
            fullWidth
            size="small"
        />
        <TextField
            label="Comment"
            value={searchValuesPrepared.comment}
            onChange={(e) => handleSearchChange('comment', e.target.value)}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                handleSearch();
                }
            }}
            fullWidth
            size="small"
        />
        <Tooltip title="Run Search">
            <Button
                variant="outlined"
                sx={{
                  height: '40px'
                }}
                onClick={handleSearch}
            >
                <Search />
            </Button>
        </Tooltip>
        <Tooltip title="Reset Search">
            <Button
                variant="outlined"
                sx={{
                  height: '40px'
                }}
                onClick={resetSearch}
            >
                <SearchOff />
            </Button>
        </Tooltip>
        <Tooltip title="Create Customer">
            <Button 
                variant="contained" 
                sx={{
                  height: '40px'
                }}
                onClick={handleCreateClick}
            >
                <Add />
            </Button>
        </Tooltip>
        <Tooltip title="Export Data">
            <Button 
                variant="contained" 
                onClick={handleExportDataClick}
                sx={{
                  height: '40px'
                }}
            >
                <UploadFile />
            </Button>
        </Tooltip>
        </Box>
      <Box sx={{ flexGrow: 1 }} />
      
      <TableComponent
        columns={columns}
        getData={getData}
        actionsHandlers={actionsHandlers}
        forceUpdate={forceUpdateCounter}
        resetPage={resetPage}
        onDataLoaded={() => setResetPage(false)}
        height='70vh'
        canExpand={(row) => row.qrCodes.length > 0}
        expandedContent={(row) => <ExpandedContent row={row} />}
      />

      <Dialog open={openCreateDialog} onClose={handleCreateCancel}>
        <DialogTitle>Create Customer</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Name"
            value={editValues.name}
            onChange={(e) => handleChange('name', e.target.value)}
            fullWidth
            margin="normal"
            error={!!validationErrors.name}
            helperText={validationErrors.name}
          />
          <TextField
            label="Phone"
            value={editValues.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            fullWidth
            margin="normal"
            error={!!validationErrors.phone}
            helperText={validationErrors.phone}
          />
          <TextField
            label="WhatsApp"
            value={editValues.whatsapp}
            onChange={(e) => handleChange('whatsapp', e.target.value)}
            fullWidth
            margin="normal"
            error={!!validationErrors.whatsapp}
            helperText={validationErrors.whatsapp}
          />
          <TextField
            label="Email"
            value={editValues.email}
            onChange={(e) => handleChange('email', e.target.value)}
            fullWidth
            margin="normal"
            error={!!validationErrors.email}
            helperText={validationErrors.email}
          />
          <TextField
            label="Comment"
            value={editValues.comment}
            onChange={(e) => handleChange('comment', e.target.value)}
            fullWidth
            multiline
            rows={4}
            margin="normal"
            error={!!validationErrors.comment}
            helperText={validationErrors.comment}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateCancel}>Cancel</Button>
          <Button onClick={handleCreateSave}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openExportDialog} onClose={handleExportDialogCancel}>
        <DialogTitle>Customers Export</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error">{error}</Alert>}
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2} sx={{ width: '100%' }}>
              {/* Horizontally: From Date, To Date */}
              <Grid item xs={12} sm={4} sx={{ display: 'flex', flex: 1 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                      label="From Date"
                      value={exportValues.fromDate}
                      format="dd.MM.yyyy"
                      slotProps={{ textField: { size: 'small'}, field: { clearable: true }}}
                      onChange={(value) => handleExportChange('fromDate', value)}
                      fullWidth
                      margin="normal"
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={4} sx={{ display: 'flex', flex: 1}}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                      label="To Date"
                      value={exportValues.endDate}
                      format="dd.MM.yyyy"
                      slotProps={{ textField: { size: 'small'}, field: { clearable: true }}}
                      onChange={(value) => handleExportChange('toDate', value)}
                      fullWidth
                      margin="normal"
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
            <Box mt={2} sx={{ width: '100%' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={exportValues.activePass}
                    onChange={(e) => handleExportChange('activePass', e.target.checked)}
                    name="activePass"
                  />
                }
                label="Active Pass"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleExportDialogCancel}>Cancel</Button>
          <Button onClick={handleExportDialogClick}>Export</Button>
        </DialogActions>
      </Dialog>

      {isQRPassCreateOpen && (
        <QRPassDialogsChain
          customer={selectedRow}
          onSuccess={(qrPass) => {
            updateData();
            setIsQRPassCreateOpen(false);
          }}
          isOpen={isQRPassCreateOpen}
          setIsOpen={setIsQRPassCreateOpen}
        />
      )}

      {isQRPassDisplayOpen && (
          <QRPassDisplayDialog
              customer={selectedRow}
              qrCode={selectedQRPass}
              open={isQRPassDisplayOpen}
              onClose={() => setIsQRPassDisplayOpen(false)}
          />
      )}

    </Box>
  );
};

export default CustomersPage;
